// E08-t4 — THE MINIMAL ADMIN PAGE.   route: /:org/settings
//
// Admin.md overview: "who is in the organization, what each role may do per KB,
//   what storage is used". Roles at launch are admin / maintainer / viewer (q1),
//   and this file never spells that list out — it renders platform ROLES.
// Admin.md f1: "Members, roles and email invites".
// Admin.md f2: "The guard matrix per KB — role by command, protected branches".
// Admin.md f3: "Quotas and per-file storage usage, live from the accounting
//   ledger" — the payload is GET /api/:org/:kb/usage verbatim
//   (git-host/src/quota.ts::usageSurface), passed in whole.
//
// THE PAGE HOLDS NO OPINION ABOUT WHO MAY DO WHAT. Every cell of the matrix is
// answered by platform/src/guards.ts::evaluateGuard against the same GuardMatrix
// the Worker enforces, so the screen and the 403 can never disagree. That also
// buys the KIND FALLBACK for free: the command vocabulary is derived per format
// (GET /api/:org/:kb/commands, Command Layer f1) and can never be enumerated by
// a matrix, so a command with no row is measured by its kind through
// ROLE_CAPABILITIES — exactly as the evaluator does it.
//
// Tenancy and Guards s2: a refusal is an ORDINARY ANSWER with the guard NAMED.
// A non-admin therefore meets the refusal in the page, next to the read-only
// matrix, instead of discovering it by clicking a control that would 403. The
// governance controls (invite, remove, matrix edit) are ABSENT for them, not
// disabled-and-hopeful — DEFAULT_GUARD_MATRIX pins invite-member, remove-member
// and set-guards to admin alone.
//
// Tenancy and Guards q3: "Allowlist beta, hard caps, no billing". The tenancy
// region SAYS that out loud, and no control on this page takes money — the prose
// exists precisely so nobody goes looking for the upgrade button.
//
// Forge f2: no free-form editor anywhere. The only writes here are governance
// actions reported to the caller as structured values (a triple, an invite, a
// member id); the page never mutates the control plane itself.
// Forge f3: nothing unrendered reaches a reader — bytes are humanised against
// their cap, counts are group-separated, and the cap is always on screen beside
// the usage because Tenancy s3 refuses "with usage shown".
//
// NO FETCHING. git-host serves only per-KB routes (/api/:org/:kb/…): there is no
// org-level members or invites endpoint yet, so the page takes DATA IN PROPS and
// the container decides where it came from. Inventing a parallel API here would
// be a lie that compiles.
import { useEffect, useMemo, useState } from 'react';

import { Badge } from '@astryxdesign/core/Badge';
import { Banner } from '@astryxdesign/core/Banner';
import { Card } from '@astryxdesign/core/Card';
import { Divider } from '@astryxdesign/core/Divider';
import { Heading } from '@astryxdesign/core/Heading';
import { Selector } from '@astryxdesign/core/Selector';
import { Stack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { TextInput } from '@astryxdesign/core/TextInput';

import {
  ROLES,
  evaluateGuard,
  type CommandKind,
  type GuardMatrix,
  type Role,
} from '../../../platform/src/guards';
// E14-t3 moved these two out of this file unchanged: Home shows the same storage
// and file totals per KB now, and one vocabulary for those numbers is the point.
import { Eyebrow } from '../components/Eyebrow';
import { humanBytes, humanCount } from '../format/units';

import './admin.css';

// E14-t6 — the four cards, the two headings levels, the role chips, the refusal
// box, the cap warning and the invite form's field/select/button were all
// hand-built out of tokens. They are now Card, Heading, Text, Badge, Banner,
// TextInput, Selector and Button.
//
// THREE THINGS DELIBERATELY STAY HAND-BUILT, and none of them is an oversight:
//   · the two TABLES. admin.test.tsx reads them as semantic tables — by role
//     table/columnheader/rowheader, with a native checkbox per (role, command)
//     cell carrying its own aria-label and disabled state. Astryx's Table is
//     data-driven (columns + renderCell) and would have to re-express that
//     structure to produce the same tree; `.dj-table` is three declarations of
//     skin on markup the app already gets right, so the skin stays.
//   · the QUOTA METERS. `role="meter"` with aria-valuenow/min/max/text is the
//     contract, and @astryxdesign/core ships no meter or progress component to
//     replace it with. A Card tinted to look like a bar would be a picture of a
//     meter, not a meter.
//   · the SECTIONS. An aria-labelled <section> is what carries role="region",
//     which is how the four surfaces are addressed; Card has no `as` prop, so the
//     section is the landmark and the Card is the surface inside it.
//
// THE ONE CONTROL ASTRYX CANNOT TAKE OVER HERE, and it is worth knowing about
// before the next page adopts Button. @astryxdesign/core's Button renders an
// UNCONDITIONAL visually-hidden live region — literally
// `<VisuallyHidden role="status" aria-live="polite">{isLoading ? 'Loading' : ''}</VisuallyHidden>`
// (node_modules/@astryxdesign/core/dist/Button/Button.js) — so every Astryx
// Button on screen adds one more element with role="status", empty or not.
// admin-route.test.tsx line 193 reads the unlanded-governance notice with
// `findByRole('status')`, which THROWS on more than one match. Swapping these two
// buttons in therefore turned that read-only contract red (3 statuses: two empty
// button regions plus the notice). web/test is a contract tree this ticket may
// not edit, so the two buttons the Admin page shows to an admin stay hand-built
// and admin.css keeps `.dj-button*` for them alone. Everything else on this page
// is the design system's. This is a REAL constraint, not a preference: any page
// that must be read by a singular findByRole('status') cannot use Astryx Button.

/** GET /api/:org/:kb/usage — git-host/src/quota.ts::usageSurface, verbatim. */
export interface UsageSurface {
  orgId: string;
  kbId: string;
  usage: { bytes: number; files: number };
  limits: { kbBytes: number; kbFiles: number };
  files: Array<{ path: string; bytes: number }>;
}

export interface AdminMember {
  userId: string;
  email: string;
  role: Role;
}

/** One entry of the format-derived vocabulary (GET /api/:org/:kb/commands). */
export interface AdminCommand {
  name: string;
  kind: CommandKind;
}

export interface AdminPageProps {
  org: { id: string; name: string };
  /** the caller, with THEIR role in this org (null = not a member) */
  viewer: { userId: string; email: string; role: Role | null };
  members: ReadonlyArray<AdminMember>;
  matrix: GuardMatrix;
  commands: ReadonlyArray<AdminCommand>;
  usage: UsageSurface;
  /** one changed cell — enough to write one guard_rules row, and no more */
  onGuardChange?: (change: { command: string; role: Role; allowed: boolean }) => void;
  onInvite?: (invite: { email: string; role: Role }) => void;
  onRemoveMember?: (member: { userId: string }) => void;
}

/** Past this share of a hard cap the page warns: the beta has no billing to
 *  absorb an overrun, so this warning is the only one anyone gets. */
const WARN_AT_PERCENT = 90;

function percentOf(now: number, cap: number): number {
  if (!(cap > 0)) return 0;
  return Math.round((now / cap) * 100);
}

const cellKey = (role: Role, command: string) => `${role}::${command}`;

export function AdminPage({
  org,
  viewer,
  members,
  matrix,
  commands,
  usage,
  onGuardChange,
  onInvite,
  onRemoveMember,
}: AdminPageProps) {
  // guards.ts: 'govern' — and with it set-guards, invite-member and
  // remove-member — belongs to admin alone.
  const isAdmin = viewer.role === 'admin';

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('viewer');

  /**
   * What the guard evaluator answers for every (role, command) pair against THIS
   * matrix. No branch is supplied, so the protected-branch stage never runs and
   * the answer is exactly the role x command decision — including the kind
   * fallback for commands the control plane never named.
   */
  const decided = useMemo(() => {
    const cells: Record<string, boolean> = {};
    for (const command of commands) {
      for (const role of ROLES) {
        cells[cellKey(role, command.name)] = evaluateGuard({
          actor: { userId: 'matrix-probe', orgId: org.id, role },
          org: { orgId: org.id },
          kb: {
            kbId: usage.kbId,
            orgId: org.id,
            // The primary format only gates a merge onto a protected branch, and
            // no branch is asked about here — so this stage is never reached.
            primaryFormat: 'docujoint',
            defaultBranch: 'main',
          },
          command: { name: command.name, kind: command.kind },
          matrix,
        }).allowed;
      }
    }
    return cells;
  }, [commands, matrix, org.id, usage.kbId]);

  /** Cells an admin has toggled since the last matrix the control plane sent.
   *  The page shows the edit immediately; landing it is the caller's job. */
  const [edits, setEdits] = useState<Record<string, boolean>>({});
  useEffect(() => setEdits({}), [decided]);

  const allows = (role: Role, command: string) =>
    edits[cellKey(role, command)] ?? decided[cellKey(role, command)] ?? false;

  function toggleCell(role: Role, command: string) {
    if (!isAdmin) return;
    const next = !allows(role, command);
    setEdits((current) => ({ ...current, [cellKey(role, command)]: next }));
    onGuardChange?.({ command, role, allowed: next });
  }

  // Tenancy s2: the refusal is the platform's own, named and worded by the
  // evaluator, so the page cannot soften or rename the guard that refused.
  const refusal = isAdmin
    ? null
    : evaluateGuard({
        actor: { userId: viewer.userId, orgId: org.id, role: viewer.role },
        org: { orgId: org.id },
        kb: {
          kbId: usage.kbId,
          orgId: org.id,
          primaryFormat: 'docujoint',
          defaultBranch: 'main',
        },
        command: { name: 'set-guards', kind: 'govern' },
        matrix,
      });

  const bytesPercent = percentOf(usage.usage.bytes, usage.limits.kbBytes);
  const filesPercent = percentOf(usage.usage.files, usage.limits.kbFiles);
  const warnings: string[] = [];
  if (bytesPercent >= WARN_AT_PERCENT) {
    warnings.push(
      `Storage is at ${bytesPercent}% of the ${humanBytes(usage.limits.kbBytes)} cap.`,
    );
  }
  if (filesPercent >= WARN_AT_PERCENT) {
    warnings.push(
      `The file count is at ${filesPercent}% of the ${humanCount(usage.limits.kbFiles)} file cap.`,
    );
  }

  return (
    <div className="dj-admin">
      <Stack as="header" direction="vertical" gap={1}>
        {/* E15-t2 — what this page IS, over the name of what it is about. The
            heading is the organization; without the eyebrow the reader has to
            infer "these are settings" from the url. */}
        <Eyebrow>Organization settings</Eyebrow>
        <Heading level={1} type="display-3">
          {org.name}
        </Heading>
        <Text as="p" type="supporting">
          Members, guards and storage. Signed in as{' '}
          {/* the address and the role are both machine facts — mono inside the
              sans sentence that frames them */}
          <Text type="code" color="inherit">
            {viewer.email}
          </Text>
          {viewer.role === null ? (
            ' (no role in this organization)'
          ) : (
            <>
              {' ('}
              <Text type="code" color="inherit">
                {viewer.role}
              </Text>
              {')'}
            </>
          )}
          .
        </Text>
      </Stack>

      {/* ── MEMBERS (Admin.md f1) ──────────────────────────────────────────── */}
      <section aria-label="Members">
       <Card padding={4} elevation="none">
        <Stack direction="vertical" gap={3}>
        <Heading level={2}>Members</Heading>
        {/* E15-t5 — the divided card header (see KnowledgeBases.tsx for the
            full note). Every card on this page is head-then-body, which is the
            shape the rule exists for. */}
        <Divider isFullBleed />
        <table className="dj-table" aria-label="Members">
          {/* E15-t2 — A COLUMN HEAD IS AN EYEBROW. The prototype spells the two
              out identically — `.lbl` and `th` are both mono, uppercase,
              letter-spaced, faint and 600 — because they do the same job: name
              the thing under them and then get out of the way. Using the
              component rather than a `thead th` rule is what keeps that a fact
              about the language instead of a coincidence between two
              stylesheets. Case comes from CSS, so `getAllByRole('columnheader')`
              still reads "Person", "Role", "Membership". */}
          <thead>
            <tr>
              <th scope="col">
                <Eyebrow>Person</Eyebrow>
              </th>
              <th scope="col">
                <Eyebrow>Role</Eyebrow>
              </th>
              {isAdmin ? (
                <th scope="col">
                  <Eyebrow>Membership</Eyebrow>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.userId}>
                {/* an address identifies a person to the control plane — mono */}
                <td>
                  <Text type="code">{member.email}</Text>
                </td>
                {/* the role VERBATIM — the launch vocabulary is guards.ts ROLES */}
                <td>
                  <Badge label={member.role} />
                </td>
                {isAdmin ? (
                  <td>
                    {/* HAND-BUILT ON PURPOSE — see THE ONE CONTROL ASTRYX
                        CANNOT TAKE OVER HERE, at the top of this file. */}
                    <button
                      type="button"
                      className="dj-button dj-button-quiet"
                      aria-label={`Remove ${member.email}`}
                      onClick={() => onRemoveMember?.({ userId: member.userId })}
                    >
                      Remove
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>

        {isAdmin ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (inviteEmail.trim() === '') return;
              onInvite?.({ email: inviteEmail.trim(), role: inviteRole });
              setInviteEmail('');
            }}
          >
            <Stack direction="horizontal" gap={3} vAlign="end" wrap="wrap">
              <TextInput
                label="Email"
                type="email"
                value={inviteEmail}
                onChange={setInviteEmail}
              />
              {/* Unlike the shell's two switchers, nothing reads this one as a
                  native select, so it is the design system's Selector. */}
              <Selector
                label="Role"
                options={[...ROLES]}
                value={inviteRole}
                onChange={(value) => setInviteRole(value as Role)}
              />
              {/* HAND-BUILT ON PURPOSE — see the top of this file. */}
              <button type="submit" className="dj-button dj-button-accent">
                Send invite
              </button>
            </Stack>
          </form>
        ) : null}
        </Stack>
       </Card>
      </section>

      {/* ── GUARDS (Admin.md f2) ───────────────────────────────────────────── */}
      <section aria-label="Guards">
       <Card padding={4} elevation="none">
        <Stack direction="vertical" gap={3}>
        <Heading level={2}>Guard matrix</Heading>
        <Divider isFullBleed />
        <Text as="p" type="supporting">
          What each role may run on{' '}
          {/* a KB id is the subject of every cell below — mono, and still
              <strong>, because it is also the sentence's emphasis */}
          <strong>
            <Text type="code" color="inherit" weight="semibold">
              {usage.kbId}
            </Text>
          </strong>
          . Commands are derived from the knowledge base’s own format, so a command the matrix
          never names is measured by its kind.
        </Text>

        {refusal === null ? null : (
          // status="error" is what gives this role="alert" — the contract
          // admin.test.tsx reads the guard's own refusal by (Tenancy s2).
          <Banner
            status="error"
            container="card"
            title={`${refusal.reason}${refusal.remedy === null ? '' : ` ${refusal.remedy.description}`}`}
          />
        )}

        <table className="dj-table" aria-label="Guard matrix">
          <thead>
            <tr>
              <th scope="col">
                <Eyebrow>Command</Eyebrow>
              </th>
              <th scope="col">
                <Eyebrow>Kind</Eyebrow>
              </th>
              {ROLES.map((role) => (
                <th scope="col" key={role}>
                  <Eyebrow>{role}</Eyebrow>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {commands.map((command) => (
              <tr key={command.name}>
                {/* E15-t2 — A COMMAND NAME AND ITS KIND ARE THE FORMAT'S OWN
                    VOCABULARY, derived per format by the control plane and never
                    written by a person (GET /api/:org/:kb/commands). Mono is
                    what says so, and it is what makes a column of them scan. */}
                <th scope="row">
                  <Text type="code" weight="semibold">
                    {command.name}
                  </Text>
                </th>
                <td>
                  <Text type="code" color="secondary">
                    {command.kind}
                  </Text>
                </td>
                {ROLES.map((role) => (
                  <td key={role} className="dj-matrix-cell">
                    <input
                      type="checkbox"
                      aria-label={`${role} may ${command.name}`}
                      checked={allows(role, command.name)}
                      disabled={!isAdmin}
                      onChange={() => toggleCell(role, command.name)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <Heading level={3} id="dj-protected-branches">
          Protected branches
        </Heading>
        {/* E15-t2 — a branch PATTERN is a glob the guard evaluates. Mono, and
            not only for looks: `main`, `release/*` and `v*.*` are strings whose
            punctuation is the whole meaning, and proportional type hides it. */}
        <ul className="dj-branches" aria-labelledby="dj-protected-branches">
          {matrix.protectedBranches.map((pattern) => (
            <li key={pattern}>
              <Text type="code">{pattern}</Text>
            </li>
          ))}
        </ul>
        <Text as="p" type="supporting">
          A merge onto one of these needs the knowledge base’s primary format gate green. Guards
          live in the control plane only — a merged changeset can never rewrite who may merge.
        </Text>
        </Stack>
       </Card>
      </section>

      {/* ── STORAGE (Admin.md f3) ──────────────────────────────────────────── */}
      <section aria-label="Storage">
       <Card padding={4} elevation="none">
        <Stack direction="vertical" gap={3}>
        <Heading level={2}>Storage</Heading>
        <Divider isFullBleed />

        {warnings.length === 0 ? null : (
          // status="warning" renders role="alert"; admin.test.tsx asserts there
          // is EXACTLY ONE alert in this region past 90% and none below it.
          <Banner
            status="warning"
            container="card"
            title={`${warnings.join(' ')} The beta has hard caps and no billing, so a write past a cap is refused rather than charged.`}
          />
        )}

        <div className="dj-gauge">
          {/* E15-t2 — THE METER'S NAME IS AN EYEBROW. This is the prototype's
              own arrangement for a meter: `.meter` is introduced by `.lbl`
              ("Derived state · 154 rows") and the numbers under it are mono. The
              label was already faint and small; the component is what makes it
              the SAME faint and small as every other region name in the app.
              The colon is load-bearing and stays: it keeps the label and the
              humanised number from reading as one word when the two spans are
              flattened into text (a screen reader, or an assertion, would
              otherwise see "used12 MB"). */}
          <Eyebrow>Bytes used:</Eyebrow>
          <div
            className="dj-meter"
            role="meter"
            aria-label="Storage"
            aria-valuenow={usage.usage.bytes}
            aria-valuemin={0}
            aria-valuemax={usage.limits.kbBytes}
            aria-valuetext={`${humanBytes(usage.usage.bytes)} of ${humanBytes(usage.limits.kbBytes)}`}
          >
            <span
              className="dj-meter-fill"
              style={{ width: `${Math.min(100, bytesPercent)}%` }}
            />
          </div>
          {/* the CAP beside the usage — Tenancy s3 refuses "with usage shown".
              E15-t2: humanised bytes against a cap and a percentage is three
              machine facts and nothing else, so the whole reading is mono; the
              two gauges then agree column by column, which is the only way a
              person sees at a glance that one is nearer its cap than the other. */}
          <Text type="code" className="dj-gauge-value">
            {humanBytes(usage.usage.bytes)} of {humanBytes(usage.limits.kbBytes)} ({bytesPercent}%)
          </Text>
        </div>

        <div className="dj-gauge">
          <Eyebrow>Files stored:</Eyebrow>
          <div
            className="dj-meter"
            role="meter"
            aria-label="Files"
            aria-valuenow={usage.usage.files}
            aria-valuemin={0}
            aria-valuemax={usage.limits.kbFiles}
            aria-valuetext={`${humanCount(usage.usage.files)} of ${humanCount(usage.limits.kbFiles)}`}
          >
            <span
              className="dj-meter-fill"
              style={{ width: `${Math.min(100, filesPercent)}%` }}
            />
          </div>
          <Text type="code" className="dj-gauge-value">
            {humanCount(usage.usage.files)} of {humanCount(usage.limits.kbFiles)} files (
            {filesPercent}%)
          </Text>
        </div>

        {/* f3 is "quotas AND PER-FILE storage usage" — the ledger holds one row
            per file on the default branch, so the reader can see what is heavy. */}
        {usage.files.length === 0 ? null : (
          <>
            <Heading level={3} id="dj-per-file">
              Per-file usage
            </Heading>
            {/* E15-t2 — a repository PATH and a byte count. Both are the
                ledger's, neither is prose, and the column of sizes on the right
                only lines up in a mono face — which is what `hasTabularNumbers`
                was approximating with figures alone. */}
            <ul className="dj-files" aria-labelledby="dj-per-file">
              {[...usage.files]
                .sort((a, b) => b.bytes - a.bytes)
                .map((file) => (
                  <li key={file.path}>
                    <Text type="code" color="primary">
                      {file.path}
                    </Text>
                    <Text type="code" color="secondary">
                      {humanBytes(file.bytes)}
                    </Text>
                  </li>
                ))}
            </ul>
          </>
        )}
        </Stack>
       </Card>
      </section>

      {/* ── TENANCY (Tenancy and Guards q3) ────────────────────────────────── */}
      <section aria-label="Tenancy">
       <Card padding={4} elevation="none">
        <Stack direction="vertical" gap={3}>
        <Heading level={2}>Tenancy</Heading>
        <Divider isFullBleed />
        <Text as="p" type="supporting">
          The forge is in an <strong>allowlist beta</strong>: organizations join by invitation and
          the quotas above are hard caps. There is <strong>no billing</strong> — nothing to pay for,
          and no way to buy a bigger cap. A write that would break one is refused, with the usage
          shown, and the caps move when beta data says they should.
        </Text>
        <Text as="p" type="supporting">
          Roles: <strong>admin</strong> governs the organization, <strong>maintainer</strong> holds
          command and merge rights, <strong>viewer</strong> reads and captures to the ledger.
        </Text>
        </Stack>
       </Card>
      </section>
    </div>
  );
}

export default AdminPage;
